import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Communication Module Service Stores
 * Updated to match backend api-types
 */
export const communicationStores: ServiceStoreConfig[] = [
  // NO `channel` SERVICE STORE HERE — deliberately. `servicePathToStoreName`
  // turns the path "channel" into the key `channelStore`, which is a RESERVED
  // built-in (builtInStores.js:73,133): the package mounts its specialized
  // ChannelStore under that exact key (RootStore.js:414). Registering the
  // service here made the two fight over one key, and the generic ServiceStore
  // that won has no `setWebsocketConnection` — so ConnectionStore.handleConnect
  // threw `channelStore.setWebsocketConnection is not a function` on EVERY
  // socket connect.
  //
  // It was also redundant: ChannelStore already owns `/channel`
  // (ChannelStore.js:37). Nothing in src/ ever wanted a generic entity list for
  // it — every consumer reads `channelStore.websocketConnection`.
  {
    // Exists and is configured, but rejects any externally-provided call by
    // design (it is internal-use-only server-side), so the boot find() always
    // errors. Not a backend gap — do not ask for it to be "fixed".
    name: "chat",
    options: {
      enableStorage: false,
      enableErrorNotifications: false,
      disableInitialFind: true,
    },
  },
  {
    name: "email-history",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.emailHistoryStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalEmails = entities.length;
          const sent = entities.filter(
            (email: Record<string, unknown>) => email.status === "sent"
          ).length;
          const failed = entities.filter(
            (email: Record<string, unknown>) => email.status === "failed"
          ).length;
          const thisWeek = entities.filter(
            (email: Record<string, unknown>) => {
              const sentDate = new Date(email.sentDate as string);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return sentDate > weekAgo;
            }
          ).length;

          return [
            {
              title: "Total Emails",
              value: () => totalEmails.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Sent",
              value: () => sent.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
            {
              title: "This Week",
              value: () => thisWeek.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "engineering-submittals",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.engineeringSubmittalsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalSubmittals = entities.length;
          const pending = entities.filter(
            (submittal: Record<string, unknown>) => submittal.status === "pending"
          ).length;
          const approved = entities.filter(
            (submittal: Record<string, unknown>) => submittal.status === "approved"
          ).length;
          const rejected = entities.filter(
            (submittal: Record<string, unknown>) => submittal.status === "rejected"
          ).length;

          return [
            {
              title: "Total Submittals",
              value: () => totalSubmittals.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Approved",
              value: () => approved.toString(),
              valueColor: "success.main",
            },
            {
              title: "Rejected",
              value: () => rejected.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "push-device-connection",
    options: { enableStorage: true, enableErrorNotifications: true },
  },
  {
    name: "push-notification",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.pushNotificationStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalNotifications = entities.length;
          const sent = entities.filter(
            (notification: Record<string, unknown>) => notification.status === "sent"
          ).length;
          const pending = entities.filter(
            (notification: Record<string, unknown>) => notification.status === "pending"
          ).length;
          const failed = entities.filter(
            (notification: Record<string, unknown>) => notification.status === "failed"
          ).length;

          return [
            {
              title: "Total Notifications",
              value: () => totalNotifications.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Sent",
              value: () => sent.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "push-trigger-send",
    // No table in this tenant (backend audit 2026-08-10) — the boot find()
    // 404s on every refresh and login.
    options: {
      enableStorage: false,
      enableErrorNotifications: false,
      disableInitialFind: true,
    },
  },
  {
    // Deregistered server-side on 2026-07-30 (no table, no migration), so this
    // 404s on every boot. NOT a pair with `rfis` — that is a different service
    // in the project domain, it is registered, and it returns real data.
    name: "rfi-attachments",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      disableInitialFind: true,
    },
  },
  {
    name: "submittal-documents",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
    },
  },
  {
    name: "support-ticket",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.supportTicketStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalTickets = entities.length;
          const openTickets = entities.filter(
            (ticket: Record<string, unknown>) =>
              ticket.status === "open" || ticket.status === "in_progress"
          ).length;
          const resolvedTickets = entities.filter(
            (ticket: Record<string, unknown>) => ticket.status === "resolved"
          ).length;
          const highPriorityTickets = entities.filter(
            (ticket: Record<string, unknown>) => ticket.priority === "high" || ticket.priority === "critical"
          ).length;

          return [
            {
              title: "Total Tickets",
              value: () => totalTickets.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Open",
              value: () => openTickets.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Resolved",
              value: () => resolvedTickets.toString(),
              valueColor: "success.main",
            },
            {
              title: "High Priority",
              value: () => highPriorityTickets.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
